'use client';

import styled from '@emotion/styled';
import React, { type ReactNode } from 'react';
import { formatFullDate } from '../../../../i18n/formatFullDate';
import {
	Css,
	H3,
	type IStyledCardProps,
	type ITheme,
	StyledCardContent,
	StyledCardPicture,
	VisuallyHidden,
} from '../../../../ui';
import Responsive from '../../../../ui/utils/Responsive';
import type { IContentCategoryEntry } from '../../../shared/api/contentful/types/IContentCategory';
import { useTranslation } from '../../../shared/i18n/translate/useTranslation';
import BaseCard, {
	CardStyled,
} from '../../../shared/components/cards/Card/BaseCard';
import { getResponsiveImageUrl } from '../../../shared/image/getResponsiveImageUrl';
import { Color } from '../../../shared/styles/Color';
import ContentCategoryTag from './ContentCategoryTag';

interface INewsCardProps extends IStyledCardProps {
	title: string | ReactNode;
	imageSource: string;
	linkHref: string;
	publicationDate?: string;
	summary?: string;
	summaryTruncated?: boolean;
	variant: 'horizontal' | 'vertical';
	category?: IContentCategoryEntry | undefined;
	categoryLabelLeftTop?: boolean;
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export default function NewsCard({
	title,
	linkHref,
	imageSource,
	publicationDate,
	summary,
	summaryTruncated,
	variant,
	category,
	categoryLabelLeftTop,
	...props
}: INewsCardProps) {
	const { t } = useTranslation();
	return (
		<NewsCardWrapper>
			<BaseCard linkHref={linkHref} variant={variant} {...props}>
				<CardPictureStyled
					source={getNewsCardImageSource(imageSource)}
					variant={variant}
					alt=""
					aria-hidden
				/>

				<CardContentStyled variant={variant}>
					{category && (
						<CategoryWrapper
							variant={variant}
							categoryLabelLeftTop={categoryLabelLeftTop}
						>
							<ContentCategoryTag>{category.fields.title}</ContentCategoryTag>
						</CategoryWrapper>
					)}
					{publicationDate && (
						<CardPublicationDate>
							{formatFullDate(publicationDate, 'MMM d, yyyy')}
						</CardPublicationDate>
					)}
					{typeof title === 'string' ? (
						<CardTitle variant={variant}>{title}</CardTitle>
					) : (
						title
					)}
					{summary && (
						<CardSummary variant={variant}>
							{summary}
							{summaryTruncated && (
								<VisuallyHidden as="span">
									, {t('web_news_alert_read_more')}
								</VisuallyHidden>
							)}
						</CardSummary>
					)}
				</CardContentStyled>
			</BaseCard>
		</NewsCardWrapper>
	);
}

function getNewsCardImageSource(imageSrc: string) {
	return getResponsiveImageUrl(imageSrc, {
		fit: 'thumb',
		w: 686,
		h: 428,
	});
}

const NewsCardWrapper = styled.div`
	display: contents;

	${CardStyled}:hover {
		transform: none;
	}
`;

const CardPictureStyled = styled(StyledCardPicture)<
	{ variant: 'horizontal' | 'vertical' },
	ITheme
>`
	overflow: hidden;
	border-radius: ${({ variant }) =>
		variant === 'horizontal' ? '12px 0 0 12px' : '12px 12px 0 0'};
	border: 1px solid ${Color.Neutral90};
	border-bottom: none;
	max-width: 100%;
	height: ${({ variant }) => (variant === 'horizontal' ? '188px' : 'auto')};
	width: ${({ variant }) => (variant === 'horizontal' ? '281px' : 'auto')};

	img {
		width: 100%;
		max-width: 100%;
		object-fit: cover;
		aspect-ratio: ${({ variant }) =>
			variant === 'horizontal' ? '281 / 189' : '64 / 40'};
	}
`;

const CardContentStyled = styled(StyledCardContent)<
	{ variant: 'horizontal' | 'vertical' },
	ITheme
>`
	flex: 1;
	justify-content: flex-start;
	gap: 5px;
	background-color: ${Color.Surface};

	padding: ${Css.pxToRem(16)};

	${CardStyled}:hover & {
		background-color: ${Color.PrimaryDefault} !important;
	}

	${CardStyled}:focus-within & {
		background-color: ${Color.PrimaryDefault} !important;
	}

	${({ variant }) =>
		variant === 'horizontal'
			? `
					border-radius: 0 12px 12px 0;
				`
			: `
					border-radius: 0 0 12px 12px;
				`}
`;

const CategoryWrapper = styled.div<{
	variant: 'horizontal' | 'vertical';
	categoryLabelLeftTop?: boolean;
}>`
	position: absolute;
	top: 8px;
	${({ variant, categoryLabelLeftTop }) =>
		variant === 'horizontal' || categoryLabelLeftTop
			? 'left: 8px;'
			: 'right: 8px;'}
	z-index: 2;
`;

const CardPublicationDate = styled.small`
	text-transform: uppercase;
	letter-spacing: 1px;
	font-size: 12px;
	letter-spacing: 0.6px;
	font-weight: 500;
	color: ${Color.Neutral30};
	${CardStyled}:focus-within & {
		color: ${Color.TextWhite};
	}
`;

export const CardTitle = styled(H3.withComponent('p'))<{
	variant: 'horizontal' | 'vertical';
}>`
	overflow: hidden;
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	line-height: 25px;
	font-size: ${({ variant }) =>
		variant === 'horizontal' ? `${Css.pxToRem(20)}` : `${Css.pxToRem(18)}`};
	color: ${Color.Background2};
	margin-bottom: 0;

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		margin-bottom: 0.1em;
	}
	${CardStyled}:focus-within & {
		color: ${Color.TextWhite};
	}
`;

const CardSummary = styled.div<{
	variant: 'horizontal' | 'vertical';
}>`
	font-size: ${Css.pxToRem(16)};
	font-weight: 300;
	line-height: ${Css.pxToRem(19)};
	color: ${Color.TextGrey};
	overflow: hidden;

	display: -webkit-box;
	-webkit-line-clamp: ${({ variant }) =>
		variant === 'horizontal' ? '4' : '3'};
	-webkit-box-orient: vertical;

	// Ensures long URLs break instead of overflowing. Prevents card from force-growing.
	overflow-wrap: break-word;
	word-break: break-word;

	${CardStyled}:focus-within & {
		color: ${Color.TextWhite};
	}

	a {
		text-decoration: underline;
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		-webkit-line-clamp: 2;
	}
`;
